// DEBUG(temp): remove this file, its AppSidebar mount point, and
// getAllFilesWarmupStatusDebug()/the backend debug/warm-status-all endpoint
// once cache warming is confirmed healthy again. Unredacted, cross-user,
// cross-file raw dump — diagnostic tool only, not for general use.
import React, { useEffect, useState } from 'react';
import { getAllFilesWarmupStatusDebug } from '../../services/api/ocel.api';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { ScrollArea } from '../ui/scroll-area';

// Diagnostic tool only — no need to poll aggressively, and each poll iterates
// every registered file's cache record on the backend (see debug_warm_status_all).
const POLL_MS = 20000;

const WarmupDebugPanel = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const res = await getAllFilesWarmupStatusDebug();
        if (cancelled) return;
        setData(res);
        setError(false);
      } catch {
        if (!cancelled) setError(true);
      }
      if (!cancelled) timer = setTimeout(load, POLL_MS);
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open]);

  const files = data?.files ? Object.entries(data.files) : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="w-full px-3 py-1 text-[11px] text-gray-400 hover:text-gray-600 hover:underline text-left"
        >
          Debug: Cache-Status (alle Dateien)
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Cache-Status — alle Dateien (Debug)</DialogTitle>
          <p className="text-xs text-amber-600">
            Temporäres Debug-Panel — wird entfernt, sobald der Cache-Aufwärmvorgang wieder funktioniert.
          </p>
        </DialogHeader>
        <ScrollArea className="h-[70vh] px-6 pb-6">
          {error && <div className="text-xs text-gray-500">Status konnte nicht geladen werden.</div>}
          {!error && !data && <div className="text-xs text-gray-500">Lade…</div>}
          {!error && files.length === 0 && data && (
            <div className="text-xs text-gray-500">Keine Dateien gefunden.</div>
          )}
          {files.length > 0 && (
            <Accordion type="multiple" className="flex flex-col gap-2">
              {files.map(([fileType, record]) => (
                <AccordionItem key={fileType} value={fileType}>
                  <AccordionTrigger>
                    <span className="flex items-center gap-2">
                      <span className="font-mono">{fileType}</span>
                      <span className="text-xs text-gray-500">({record.status || 'unknown'})</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="text-xs space-y-3">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <span className="text-gray-500">started_at</span>
                        <span className="font-mono">{record.started_at || '—'}</span>
                        <span className="text-gray-500">finished_at</span>
                        <span className="font-mono">{record.finished_at || '—'}</span>
                        <span className="text-gray-500">progress</span>
                        <span className="font-mono">
                          {record.progress ? `${record.progress.completed}/${record.progress.total}` : '—'}
                        </span>
                        <span className="text-gray-500">skipped</span>
                        <span className="font-mono">{record.skipped ?? '—'}</span>
                      </div>

                      {Array.isArray(record.configs_included) && record.configs_included.length > 0 && (
                        <div>
                          <div className="font-medium text-gray-600 mb-1">configs_included</div>
                          <ul className="font-mono space-y-0.5">
                            {record.configs_included.map((c, i) => (
                              <li key={i}>
                                {c.user_id} — {c.active_file || '(kein active_file)'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(record.configs_excluded) && record.configs_excluded.length > 0 && (
                        <div>
                          <div className="font-medium text-gray-600 mb-1">configs_excluded</div>
                          <ul className="font-mono space-y-0.5">
                            {record.configs_excluded.map((c, i) => (
                              <li key={i}>
                                {c.user_id} — {c.active_file || '(kein active_file)'} ({c.reason})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(record.in_flight) && record.in_flight.length > 0 && (
                        <div>
                          <div className="font-medium text-gray-600 mb-1">in_flight</div>
                          <ul className="font-mono space-y-0.5">
                            {record.in_flight.map((t, i) => (
                              <li key={i}>
                                {t.name} — sources: {JSON.stringify(t.sources)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(record.target_log) && record.target_log.length > 0 && (
                        <div>
                          <div className="font-medium text-gray-600 mb-1">target_log</div>
                          <table className="w-full text-left font-mono">
                            <thead>
                              <tr className="text-gray-500">
                                <th className="pr-2">name</th>
                                <th className="pr-2">status</th>
                                <th className="pr-2">elapsed</th>
                                <th className="pr-2">sources</th>
                                <th>error</th>
                              </tr>
                            </thead>
                            <tbody>
                              {record.target_log.map((t, i) => (
                                <tr key={i}>
                                  <td className="pr-2">{t.name}</td>
                                  <td className="pr-2">{t.status}</td>
                                  <td className="pr-2">{t.elapsed != null ? t.elapsed.toFixed(2) : '—'}</td>
                                  <td className="pr-2">{JSON.stringify(t.sources)}</td>
                                  <td className="text-red-500">{t.error || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default WarmupDebugPanel;
